import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, ShoppingCart, Loader2, Building2 } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNewSystem, setIsNewSystem] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const { login, subdomain, companyInfo } = useAuth();
  const navigate = useNavigate();

  // Check if system needs setup
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API}/api/auth/setup-status`);
        if (response.ok) {
          const data = await response.json();
          setIsNewSystem(!data.is_setup);
        }
      } catch (error) {
        console.log('Setup check failed');
      } finally {
        setCheckingSetup(false);
      }
    };
    checkSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('تم تسجيل الدخول بنجاح');
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Determine display name and colors based on company
  const systemName = companyInfo?.name || 'نظام شامل للمشتريات';
  const isCompanyPortal = !!companyInfo;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 ${isCompanyPortal ? 'bg-gradient-to-br from-emerald-600 to-teal-600' : 'bg-gradient-to-br from-blue-600 to-indigo-600'} rounded-2xl shadow-lg mb-4`}>
            {isCompanyPortal ? (
              <Building2 className="w-8 h-8 text-white" />
            ) : (
              <ShoppingCart className="w-8 h-8 text-white" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-800">{systemName}</h1>
          <p className="text-gray-500 mt-2">
            {isCompanyPortal ? 'بوابة الشركة' : 'منصة إدارة المشاريع والمشتريات'}
          </p>
        </div>

        {/* Company Badge */}
        {isCompanyPortal && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
            <p className="text-sm text-emerald-700">
              <Building2 className="w-4 h-4 inline ml-1" />
              مرحباً بك في بوابة <strong>{companyInfo.name}</strong>
            </p>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Show setup notice for new system */}
          {isNewSystem && !checkingSetup && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-800 font-medium mb-2">🎉 مرحباً! هذا النظام جديد</p>
              <p className="text-sm text-amber-700">
                قم بإنشاء حساب مدير النظام للبدء.{' '}
                <Link to="/register" className="text-amber-900 underline font-medium">
                  إنشاء حساب
                </Link>
              </p>
            </div>
          )}

          <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">تسجيل الدخول</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                البريد الإلكتروني
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                placeholder="example@company.com"
                required
                disabled={loading}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                كلمة المرور
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none pl-12"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full ${isCompanyPortal ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'} text-white py-3 rounded-xl font-medium transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري تسجيل الدخول...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  تسجيل الدخول
                </>
              )}
            </button>
          </form>

          {/* Only show register link for main portal AND only when system is brand new */}
          {!isCompanyPortal && isNewSystem && (
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                ليس لديك حساب؟{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  إنشاء حساب جديد
                </Link>
              </p>
            </div>
          )}
          {!isCompanyPortal && !isNewSystem && (
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-xs">
                للحصول على حساب جديد، يرجى التواصل مع مدير النظام
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          جميع الحقوق محفوظة © 2025
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
